import type {
  LightboxConfig,
  MoamalatCompleteData,
  PaymentCancelMessage,
  PaymentCompleteMessage,
  PaymentErrorMessage,
} from './types';
import { parsePaymentParams, showError } from './utils';

/**
 * Development-only logging utility
 */
const devLog = (...args: unknown[]) => {
  if (window.__PAYMENT_ENV__ === 'development') {
    // biome-ignore lint/suspicious/noConsole: Development logging is intentional
    console.log(...args);
  }
};

const devTable = (data: unknown) => {
  if (window.__PAYMENT_ENV__ === 'development') {
    // biome-ignore lint/suspicious/noConsole: Development logging is intentional
    console.table(data);
  }
};

/**
 * Main payment relay application
 */
class PaymentRelay {
  private completedCalled = false;
  private cancelledCalled = false;

  async init(): Promise<void> {
    try {
      // Decrypt the URL parameters
      const params = await parsePaymentParams();

      if (!params) {
        showError(
          'معلومات الدفع غير مكتملة. الرجاء التأكد من صحة الرابط والمحاولة مرة أخرى.',
        );
        return;
      }

      // Log received parameters in development
      devLog(
        '%c[Payment Relay] Received Parameters from Store App:',
        'color: #10b981; font-weight: bold',
      );
      devTable({
        merchantId: params.merchantId,
        terminalId: params.terminalId,
        transactionId: params.transactionId,
      });

      // Wait for Lightbox SDK to be available
      await this.waitForLightbox();

      // Initialize payment
      await this.initializePayment(params);
    } catch (error) {
      showError(
        'حدث خطأ أثناء تحميل بوابة الدفع. الرجاء المحاولة مرة أخرى لاحقاً.',
      );
      this.sendErrorMessage(
        error instanceof Error ? error.message : 'Unknown error',
      );
    }
  }

  private async waitForLightbox(): Promise<void> {
    return new Promise((resolve, reject) => {
      const maxAttempts = 50; // 5 seconds max
      let attempts = 0;

      const checkLightbox = (): void => {
        if (window.Lightbox?.Checkout) {
          resolve();
          return;
        }

        attempts++;
        if (attempts >= maxAttempts) {
          reject(new Error('Lightbox SDK failed to load'));
          return;
        }

        setTimeout(checkLightbox, 100);
      };

      checkLightbox();
    });
  }

  private async initializePayment(
    params: Awaited<ReturnType<typeof parsePaymentParams>>,
  ): Promise<void> {
    if (!params) return;

    // The backend signed these; they go to Lightbox exactly as received.
    const amount = Number(params.amountTrxn);
    const dateTime = params.trxDateTime;
    const merchantReference = params.merchantReference;
    const secureHash = params.secureHash;

    // Log Lightbox configuration in development
    devLog(
      '%c[Payment Relay] Lightbox Configuration:',
      'color: #3b82f6; font-weight: bold',
    );
    devTable({
      MID: params.merchantId,
      TID: params.terminalId,
      AmountTrxn: amount,
      MerchantReference: merchantReference,
      TrxDateTime: dateTime,
      SecureHash: secureHash,
    });

    // Configure Lightbox
    const config: LightboxConfig = {
      MID: params.merchantId,
      TID: params.terminalId,
      AmountTrxn: amount,
      MerchantReference: merchantReference,
      TrxDateTime: dateTime,
      SecureHash: secureHash,
      completeCallback: (data: MoamalatCompleteData) =>
        this.handleComplete(data, params.transactionId),
      errorCallback: () => {
        // Error handling done in parent
      },
      cancelCallback: () => this.handleCancel(),
    };

    // Check if already configured (reconfiguration case)
    if (window.Lightbox.Checkout.configure?.MID) {
      // Update existing configuration
      window.Lightbox.Checkout.configure.AmountTrxn = config.AmountTrxn;
      window.Lightbox.Checkout.configure.MerchantReference =
        config.MerchantReference;
      window.Lightbox.Checkout.configure.TrxDateTime = config.TrxDateTime;
      window.Lightbox.Checkout.configure.SecureHash = config.SecureHash;
    } else {
      // Initial configuration
      window.Lightbox.Checkout.configure = config;
    }

    // Show the Lightbox payment dialog
    window.Lightbox.Checkout.showLightbox();
  }

  private handleComplete(
    data: MoamalatCompleteData,
    transactionId: string,
  ): void {
    // Prevent duplicate calls
    if (this.completedCalled) {
      return;
    }
    this.completedCalled = true;

    // Close the lightbox
    window.Lightbox.Checkout.closeLightbox();

    // Send success message to parent window
    const message: PaymentCompleteMessage = {
      method: 'Complete',
      moamalat: {
        transId: transactionId,
        SystemReference: data.SystemReference,
        PayerAccount: data.PayerAccount,
      },
    };

    window.parent.postMessage(message, '*');
  }

  private handleCancel(): void {
    // Prevent duplicate calls
    if (this.cancelledCalled) {
      return;
    }
    this.cancelledCalled = true;

    // Send cancellation message to parent window
    const message: PaymentCancelMessage = {
      method: 'Cancelled',
    };

    window.parent.postMessage(message, '*');
  }

  private sendErrorMessage(error: string): void {
    const message: PaymentErrorMessage = {
      method: 'Error',
      error,
    };

    window.parent.postMessage(message, '*');
  }
}

// Initialize the app when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const app = new PaymentRelay();
    app.init();
  });
} else {
  const app = new PaymentRelay();
  app.init();
}
